import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import FareGuarantee from '../components/FareGuarantee';
import PopularRoutes from '../components/PopularRoutes';
import HowItWorks from '../components/HowItWorks';
import Testimonials from '../components/Testimonials';
import CtaBanner from '../components/CtaBanner';
import SEO from '../components/SEO';

const jsonLd = {
  '@context':'https://schema.org','@type':'WebSite',name:'USAFlightFare',url:'https://usaflightfare.com',
  description:'USAFlightFare is an independent travel agency helping travelers find and book domestic and international flights with transparent pricing and 24/7 support.',
  potentialAction:{'@type':'SearchAction',target:'https://usaflightfare.com/flights?origin={origin}&destination={destination}&date={date}&adults=1','query-input':'required name=origin required name=destination required name=date'},
};

const WHY_CHOOSE = [
  { icon:'🔍', title:'Transparent Pricing', desc:'We believe travelers deserve clear information before making a booking decision. Our pricing and service-related information are disclosed throughout the booking process so customers can understand applicable charges and travel terms.' },
  { icon:'🎧', title:'Professional Travel Assistance', desc:'Our experienced travel support team assists customers with booking inquiries, itinerary reviews, travel information, and general flight-related questions.' },
  { icon:'✈️', title:'Domestic & International Travel Support', desc:'Whether you are traveling within the United States or planning an international trip, we provide assistance for a wide range of travel requirements.' },
  { icon:'💬', title:'Customer-Focused Service', desc:'We strive to deliver responsive customer support and help travelers navigate airline policies, schedule changes, and travel-related concerns whenever possible.' },
  { icon:'🔒', title:'Secure Booking Experience', desc:'Protecting customer information is important to us. We use industry-standard security measures to help safeguard personal information submitted through our website.' },
];

const SUPPORT_ITEMS = [
  'Flight booking assistance',
  'Travel itinerary questions',
  'General airline policy guidance',
  'Schedule change inquiries',
  'Cancellation information',
  'Refund-related guidance',
  'Travel documentation questions',
];

const FAQS = [
  { q:'Is USAFlightFare an airline?', a:'No. USAFlightFare is an independent travel agency and is not an airline.' },
  { q:'Are you affiliated with any airline?', a:'No. We are not affiliated with, endorsed by, or associated with any airline.' },
  { q:'Can flight prices change?', a:'Yes. Airline fares are subject to change until booking confirmation and ticket issuance.' },
  { q:'Are refunds guaranteed?', a:'Refund eligibility depends on airline policies, fare conditions, and applicable travel provider rules.' },
  { q:'Can I modify my reservation?', a:'Modification options depend on airline rules and fare restrictions associated with your booking.' },
  { q:'Do you charge service fees?', a:'Applicable service fees, if any, are disclosed during the booking process and are further detailed on our Service Fees page.' },
  { q:'How can I contact customer support?', a:'You can reach our customer support team through the contact methods listed on our Contact Us page.' },
  { q:'Do you provide international travel assistance?', a:'Yes. We assist customers with both domestic and international travel inquiries.' },
  { q:'What documents are required for travel?', a:'Required travel documents vary by destination, citizenship status, and airline requirements.' },
  { q:'Where can I review your policies?', a:'You can review our Privacy Policy, Terms & Conditions, Refund Policy, and Service Fees pages directly through our website.' },
];

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div>
      <SEO
        title="Domestic & International Flight Booking Services"
        canonical="/"
        description="USAFlightFare is an independent travel agency providing flight booking assistance, travel support, and transparent pricing for domestic and international travel."
        keywords="flight booking assistance, travel support USA, domestic flights, international flights, independent travel agency, flight reservations, airline booking help"
        jsonLd={jsonLd}
      />
      <HeroSection />

      {/* Intro Text */}
      <section className="bg-white py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-gray-600 leading-relaxed text-base sm:text-lg mb-4">
            Whether you are planning a family vacation, business trip, weekend getaway, or international journey, our team is here to assist you throughout the booking process.
          </p>
          <p className="text-gray-600 leading-relaxed text-base sm:text-lg mb-4">
            We understand that travel planning can be overwhelming. Flight schedules, fare changes, airline policies, baggage rules, and cancellation terms often create confusion for travelers. Our goal is to simplify the process by providing professional booking assistance and travel support tailored to your needs.
          </p>
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 text-sm text-amber-800 leading-relaxed">
            <strong>Please note:</strong> USAFlightFare is not affiliated with, endorsed by, or associated with any airline. We operate as an independent travel service provider and work to help customers explore available travel options and make informed booking decisions.
          </div>
        </div>
      </section>

      <FareGuarantee />

      {/* Why Choose USAFlightFare */}
      <section className="bg-gray-50 py-14 sm:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold text-emerald-700 uppercase tracking-widest bg-emerald-50 border border-emerald-100 px-4 py-1.5 rounded-full mb-3">Our Commitment</span>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">Why Choose USAFlightFare</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_CHOOSE.map(item => (
              <div key={item.title} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PopularRoutes />
      <HowItWorks />

      {/* Customer Support Section */}
      <section className="bg-white py-14 sm:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-bold text-emerald-700 uppercase tracking-widest bg-emerald-50 border border-emerald-100 px-4 py-1.5 rounded-full mb-3">Here to Help</span>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">Customer Support You Can Trust</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm sm:text-base">Travel plans sometimes change unexpectedly. Our customer support team works to assist travelers with general booking inquiries and travel-related information.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div>
              <p className="text-gray-700 font-semibold mb-4">Support may be available for:</p>
              <ul className="space-y-3">
                {SUPPORT_ITEMS.map(item => (
                  <li key={item} className="flex items-center gap-3 text-gray-600 text-sm">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">✔</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-6 text-sm text-amber-800 leading-relaxed">
              <p className="font-semibold mb-2">Important Notice</p>
              <p>Final decisions regarding schedule changes, cancellations, refunds, baggage allowances, and ticket conditions are governed by the applicable airline and fare rules.</p>
              <div className="mt-4 pt-4 border-t border-amber-200">
                <a href="tel:+18885098604" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-xl transition-colors text-sm">
                  📞 Call Support: +1 (888) 509-8604
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Testimonials />

      {/* FAQ Section */}
      <section className="bg-gray-50 py-14 sm:py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-bold text-emerald-700 uppercase tracking-widest bg-emerald-50 border border-emerald-100 px-4 py-1.5 rounded-full mb-3">FAQs</span>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center font-semibold text-gray-800 hover:bg-gray-50 transition-colors text-sm"
                >
                  {faq.q}
                  <span className={`text-gray-400 ml-4 shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>▼</span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 pt-1 text-gray-600 text-sm leading-relaxed border-t border-gray-50">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 text-xs mt-8">
            You can review our{' '}
            <Link to="/privacy-policy" className="text-emerald-600 hover:underline">Privacy Policy</Link>,{' '}
            <Link to="/terms" className="text-emerald-600 hover:underline">Terms & Conditions</Link>,{' '}
            <Link to="/refund-policy" className="text-emerald-600 hover:underline">Refund Policy</Link>, and{' '}
            <Link to="/service-fees" className="text-emerald-600 hover:underline">Service Fees</Link> pages directly through our website.
          </p>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
